import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { zodSupplierLetterMetaSchema } from '../validations';

import {
  type SupplierLetterMeta,
  type UploadSupplierLetterRequest,
} from './types';
import { zodUploadSupplierLetterRequestSchema } from './validations';

/**
 * Uploads or replaces the supplier authorization letter for a Company.
 *
 * @param companyId - The ID of the company.
 * @param body - The file metadata for the supplier letter.
 * @returns The stored supplier letter metadata.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/put_company_supplier_letter_vadmin_companies__company_id__supplier_letter_put
 */
// fallow-ignore-next-line complexity
export const putCompanySupplierLetter = async (
  companyId: UUID,
  body: UploadSupplierLetterRequest,
) => {
  try {
    return await vestigasApi
      .put(ENDPOINT.COMPANY.SUPPLIER_LETTER.PUT(companyId), {
        json: body,
        validate: {
          body: zodUploadSupplierLetterRequestSchema,
          response: zodSupplierLetterMetaSchema,
        },
      })
      .json<SupplierLetterMeta>();
  } catch (error) {
    log.error({
      message: 'Error uploading company supplier letter',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(
            ($) => $.company.putCompanySupplierLetter.error422,
            { ns: 'data' },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.company.putCompanySupplierLetter.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    throw error;
  }
};
